import { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../utils/firebase';
import { signInWithCustomToken, signOut, onAuthStateChanged } from 'firebase/auth';
import { 
  loginSuperAdmin, 
  registerSuperAdmin, 
  getSuperAdminProfile 
} from '../utils/superAdminApi';

const SuperAdminContext = createContext();

export function SuperAdminProvider({ children }) {
  const [superAdmin, setSuperAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('[SuperAdminContext] Auth state changed:', firebaseUser?.email);
      
      if (firebaseUser) {
        // Check if user is a super admin by trying to get profile
        try {
          const profile = await getSuperAdminProfile();
          setSuperAdmin({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            ...profile.superAdmin
          });
        } catch (err) {
          console.log('[SuperAdminContext] User is not a super admin:', err.message);
          setSuperAdmin(null);
        }
      } else {
        setSuperAdmin(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await loginSuperAdmin(email, password);
      
      // Sign in with Firebase using custom token
      await signInWithCustomToken(auth, response.customToken);
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await registerSuperAdmin(email, password, name);
      
      // Sign in with Firebase using custom token
      await signInWithCustomToken(auth, response.customToken);
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setSuperAdmin(null);
      setError(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return (
    <SuperAdminContext.Provider value={{ 
      superAdmin, 
      loading, 
      error,
      login, 
      register, 
      logout 
    }}>
      {children}
    </SuperAdminContext.Provider>
  );
}

export function useSuperAdmin() {
  return useContext(SuperAdminContext);
}

export default SuperAdminContext; 